import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { switchMap, catchError, filter, tap, takeUntil } from 'rxjs/operators';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { LanguageService } from 'src/app/content-management/shared/services/language.service';
import { ArticleContentTypeService } from 'src/app/content/services/article-content-type.service';
import { IArticleContentDto } from 'src/app/core/dtos/interfaces/i-article-content-dto';
import { ContentTypes } from 'src/app/shared/enums/content-types';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { FormGroup } from '@angular/forms';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { ContentStateService } from 'src/app/content/services/content-state.service';
import { IContentConflictForm } from 'src/app/shared-form-sections/interfaces/i-content-conflict-form';
import { WithConflictsSection } from 'src/app/shared/mixins/with-conflicts';
import { ILanguage } from 'src/app/core/models/i-language';
import { IDeliveryTarget } from 'src/app/core/models/i-delivery-target';

class ArticleContentMergeConflictComponentBase {
  destroy$ = new Subject<boolean>();
  isInitialContentMergingMode: boolean = false;
  isChangedContentMergingMode: boolean = false;
  initialContentToCompare = [];
  changedContentToCompare = [];
  changedArticleContent: IArticleContentDto;
  initialContentForm: FormGroup<IContentConflictForm>;
  changedContentForm: FormGroup<IContentConflictForm>;
  initialArticleContent: IArticleContentDto;

  constructor(
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public parentInjector: Injector
  ) {}
}
const ArticleContentMergeConflictMixinBase = WithProcessing(
  WithConflictsSection(ArticleContentMergeConflictComponentBase)
);

@Component({
  selector: 'app-article-content-merge-conflict',
  templateUrl: './article-content-merge-conflict.component.html',
  styleUrls: ['./article-content-merge-conflict.component.scss'],
})
export class ArticleContentMergeConflictComponent
  extends ArticleContentMergeConflictMixinBase
  implements OnInit, OnDestroy
{
  destroy$ = new Subject<boolean>();
  articleContent$: any;
  initialArticleContent: IArticleContentDto;
  id: string;
  isSaving: boolean;
  isInitialContentMergingMode: boolean = false;
  isChangedContentMergingMode: boolean = false;
  initialContentToCompare = [];
  changedContentToCompare = [];
  changedArticleContent: IArticleContentDto;
  initialContentForm: FormGroup<IContentConflictForm>;
  changedContentForm: FormGroup<IContentConflictForm>;
  languages$: Observable<ILanguage[]>;
  allLanguages: ILanguage[];
  deliveryTargets: IDeliveryTarget[];
  isProcessingDifference: boolean = false;
  constructor(
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public articleService: ArticleContentTypeService,
    public _dialogService: DialogService,
    private _activatedRoute: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _contentStateService: ContentStateService,
    public parentInjector: Injector
  ) {
    super(languageService, deliveryTargetsService, parentInjector);
  }
  ngOnInit(): void {
    this.process$(
      forkJoin({
        languages: this.languageService.getAll$(),
        deliveryTargets: this.deliveryTargetsService.getFiltered$(),
      })
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ languages, deliveryTargets }) => {
          this.allLanguages = languages;
          this.deliveryTargets = deliveryTargets;
          this.initContentWithConflicts();
        },
      });
  }
  initContentWithConflicts(): void {
    this.isProcessingDifference = true;
    this.process$(
      combineLatest([
        this._activatedRoute.paramMap.pipe(
          filter((paramMaps) => !!paramMaps.get('id')),
          switchMap((paramsMap) => {
            this.id = paramsMap.get('id');
            return this.process$(this.articleService.getById$(this.id));
          }),
          takeUntil(this.destroy$),
          catchError(() => {
            this._dialogService.openErrorDialogAndNavigate(
              {
                message:
                  'An error has occurred loading the article. Please contact IT.',
              },
              'dashboard'
            );
            return of(null);
          })
        ),
        this._contentStateService.getContentDto$(),
      ])
    ).subscribe({
      next: ([articleContentDto, changedContentVersion]) => {
        this.isProcessingDifference = false;
        if (articleContentDto !== null && changedContentVersion !== null) {
          this._initInitialContentVersion(
            articleContentDto,
            this.allLanguages,
            this.deliveryTargets
          );
          this._initChangedContentVersion(
            changedContentVersion,
            this.allLanguages,
            this.deliveryTargets
          );
        }
      },
    });
  }

  setChangedContentMergingMode() {
    this.isChangedContentMergingMode = !this.isChangedContentMergingMode;
    if (this.isChangedContentMergingMode) {
      this.changedContentForm.controls.body.enable();
    } else {
      this.changedContentForm.controls.body.disable();
    }
  }
  setInitialContentMergingMode() {
    this.isInitialContentMergingMode = !this.isInitialContentMergingMode;
    if (this.isInitialContentMergingMode) {
      this.initialContentForm.controls.body.enable();
    } else {
      this.initialContentForm.controls.body.disable();
    }
  }
  performArticleSave(formChanges: FormGroup<IContentConflictForm>) {
    this.isSaving = true;
    const contentToSave = {
      ...this.initialArticleContent,
      ...formChanges.getRawValue(),
    };
    this.process$(this.articleService.update$(this.id, contentToSave))
      .pipe(
        catchError((error) => {
          this._dialogService.openErrorDialog({
            message:
              'An error has occurred whilst saving the article. Please contact IT.',
          });
          this.isSaving = false;
          return EMPTY;
        }),
        tap((refreshedDto) => {
          this._snackBar.open('Article saved successfully', null, {
            duration: 5 * 1000,
          });
        })
      )
      .subscribe({
        next: () => {
          this._router.navigate([
            '/content',
            ContentTypeRouterPaths[ContentTypes.ARTICLE],
            this.id,
            'edit',
          ]);
        },
        complete: () => {
          this.isSaving = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
